import type {
  Action,
  CheckRunExpandableRowAction,
  CheckRunExpandableRowState,
  PaymentFileExpandableRowAction,
  PaymentFileExpandableRowState,
  State
} from './types'

import { uniquePredicate } from './functions'

export const initialState: State = {
  searchQuery: '',

  expandedRowIds: [],

  selectedCheckRunIds: [],
  selectedPaymentFileIds: [],
  selectedEOBIds: [],
  selectedClaimIds: [],

  releaseSettingsModal: {
    isOpen: false,
    isSubmitting: false
  },

  deleteCheckRunsModal: {
    isOpen: false,
    checkRunIds: [],
    isSubmitting: false
  },

  rerunCheckRunModal: {
    isOpen: false,
    checkRunIds: []
  },

  dropoffModal: {
    isOpen: false
  },

  filters: {
    statusIds: [],
    locationIds: [],
    date: {},
    scheduledReleaseDate: {},
    flags: [],
    releaseType: [],
    groupNumber: undefined,
    groupName: undefined,
    amount: []
  },

  pagination: {
    currentPage: 0,
    pageSize: 10
  },

  liveUpdatesEnabled: true
}

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'releaseSettingsModal/open': {
      return {
        ...state,
        releaseSettingsModal: {
          ...state.releaseSettingsModal,
          isOpen: true
        }
      }
    }

    case 'releaseSettingsModal/close': {
      return {
        ...state,
        releaseSettingsModal: {
          ...state.releaseSettingsModal,
          isOpen: false
        }
      }
    }

    case 'releaseSettingsModal/submit': {
      return {
        ...state,
        releaseSettingsModal: {
          ...state.releaseSettingsModal,
          isSubmitting: true,
          error: undefined
        }
      }
    }

    case 'releaseSettingsModal/submit/success': {
      return {
        ...state,
        releaseSettingsModal: {
          ...state.releaseSettingsModal,
          isOpen: false,
          isSubmitting: false,
          error: undefined
        }
      }
    }

    case 'releaseSettingsModal/submit/error': {
      return {
        ...state,
        releaseSettingsModal: {
          ...state.releaseSettingsModal,
          isSubmitting: false,
          error: action.payload
        }
      }
    }

    case 'deleteCheckRunsModal/open': {
      return {
        ...state,
        deleteCheckRunsModal: {
          ...state.deleteCheckRunsModal,
          isOpen: true,
          checkRunIds: action.payload
        }
      }
    }

    case 'deleteCheckRunsModal/close': {
      return {
        ...state,
        deleteCheckRunsModal: {
          ...state.deleteCheckRunsModal,
          isOpen: false,
          checkRunIds: []
        }
      }
    }

    case 'deleteCheckRunsModal/submit': {
      return {
        ...state,
        deleteCheckRunsModal: {
          ...state.deleteCheckRunsModal,
          isSubmitting: true,
          error: undefined
        }
      }
    }

    case 'deleteCheckRunsModal/submit/success': {
      return {
        ...state,
        deleteCheckRunsModal: {
          ...state.deleteCheckRunsModal,
          isOpen: false,
          isSubmitting: false,
          error: undefined
        }
      }
    }

    case 'deleteCheckRunsModal/submit/error': {
      return {
        ...state,
        deleteCheckRunsModal: {
          ...state.deleteCheckRunsModal,
          isSubmitting: false,
          error: action.payload
        }
      }
    }

    case 'rerunCheckRunModal/open': {
      return {
        ...state,
        rerunCheckRunModal: {
          ...state.rerunCheckRunModal,
          isOpen: true,
          checkRunIds: action.payload
        }
      }
    }

    case 'rerunCheckRunModal/close': {
      return {
        ...state,
        rerunCheckRunModal: {
          ...state.rerunCheckRunModal,
          isOpen: false,
          checkRunIds: []
        }
      }
    }

    case 'dropoffModal/open': {
      return {
        ...state,
        dropoffModal: {
          ...state.dropoffModal,
          isOpen: true,
          checkRun: action.payload
        }
      }
    }

    case 'dropoffModal/close': {
      return {
        ...state,
        dropoffModal: {
          ...state.dropoffModal,
          isOpen: false
        }
      }
    }

    case 'rows/expand': {
      return {
        ...state,
        expandedRowIds: action.payload
      }
    }

    case 'row/expand': {
      return {
        ...state,
        expandedRowIds: [...state.expandedRowIds, action.payload]
          .sort((a, b) => a.localeCompare(b))
          .filter((v, i, a) => a.indexOf(v) === i)
      }
    }

    case 'row/collapse': {
      return {
        ...state,
        expandedRowIds: state.expandedRowIds.filter(id => id !== action.payload)
      }
    }

    case 'checkRunIds/select': {
      return {
        ...state,
        selectedCheckRunIds: action.payload
          .sort((a, b) => a.localeCompare(b))
          .filter(uniquePredicate)
      }
    }

    case 'paymentFileIds/select': {
      return {
        ...state,
        selectedPaymentFileIds: action.payload
          .sort((a, b) => a.localeCompare(b))
          .filter(uniquePredicate)
      }
    }

    case 'eobIds/select': {
      return {
        ...state,
        selectedEOBIds: action.payload.sort((a, b) => a.localeCompare(b)).filter(uniquePredicate)
      }
    }

    case 'claimIds/select': {
      return {
        ...state,
        selectedClaimIds: action.payload.sort((a, b) => a.localeCompare(b)).filter(uniquePredicate)
      }
    }

    case 'pagination/currentPage/set': {
      return {
        ...state,
        expandedRowIds: [],
        pagination: {
          ...state.pagination,
          currentPage: action.payload
        }
      }
    }

    case 'pagination/pageSize/set': {
      return {
        ...state,
        expandedRowIds: [],
        pagination: {
          ...state.pagination,
          currentPage: 0,
          pageSize: action.payload
        }
      }
    }

    case 'filters/set': {
      return {
        ...state,
        filters: action.payload,
        pagination: {
          ...state.pagination,
          currentPage: 0
        }
      }
    }

    case 'filters/clear': {
      return {
        ...state,
        filters: initialState.filters,
        pagination: {
          ...state.pagination,
          currentPage: 0
        }
      }
    }

    case 'searchQuery/set': {
      return {
        ...state,
        searchQuery: action.payload,
        pagination: {
          ...state.pagination,
          currentPage: 0
        }
      }
    }

    case 'refreshInterval/set': {
      return {
        ...state,
        refreshInterval: action.payload
      }
    }

    case 'refreshInterval/clear': {
      return {
        ...state,
        refreshInterval: undefined
      }
    }

    case 'liveUpdatesEnabled/set': {
      return {
        ...state,
        liveUpdatesEnabled: action.payload
      }
    }

    default: {
      return state
    }
  }
}

export function checkRunExpandableRowReducer(
  state: CheckRunExpandableRowState,
  action: CheckRunExpandableRowAction
): CheckRunExpandableRowState {
  switch (action.type) {
    case 'rows/expand': {
      return {
        ...state,
        expandedRowIds: action.payload.sort((a, b) => a.localeCompare(b)).filter(uniquePredicate)
      }
    }

    case 'row/expand': {
      return {
        ...state,
        expandedRowIds: [...state.expandedRowIds, action.payload]
          .sort((a, b) => a.localeCompare(b))
          .filter(uniquePredicate)
      }
    }

    case 'row/collapse': {
      return {
        ...state,
        expandedRowIds: state.expandedRowIds
          .filter(id => id !== action.payload)
          .sort((a, b) => a.localeCompare(b))
          .filter(uniquePredicate)
      }
    }

    default: {
      return state
    }
  }
}

export function paymentFileExpandableRowReducer(
  state: PaymentFileExpandableRowState,
  action: PaymentFileExpandableRowAction
): PaymentFileExpandableRowState {
  switch (action.type) {
    case 'rows/expand': {
      return {
        ...state,
        expandedRowIds: action.payload.sort((a, b) => a.localeCompare(b)).filter(uniquePredicate)
      }
    }

    case 'row/expand': {
      return {
        ...state,
        expandedRowIds: [...state.expandedRowIds, action.payload]
          .sort((a, b) => a.localeCompare(b))
          .filter(uniquePredicate)
      }
    }

    case 'row/collapse': {
      return {
        ...state,
        expandedRowIds: state.expandedRowIds
          .filter(id => id !== action.payload)
          .sort((a, b) => a.localeCompare(b))
          .filter(uniquePredicate)
      }
    }

    case 'onHoldModal/open': {
      return {
        ...state,
        onHoldModal: {
          ...state.onHoldModal,
          form: {
            ...state.onHoldModal.form,
            reason: ''
          },
          isOpen: true,
          eobIds: action.payload
        }
      }
    }

    case 'onHoldModal/close': {
      return {
        ...state,
        onHoldModal: {
          ...state.onHoldModal,
          isOpen: false,
          eobIds: []
        }
      }
    }

    case 'onHoldModal/submit': {
      return {
        ...state,
        onHoldModal: {
          ...state.onHoldModal,
          isSubmitting: true,
          error: undefined
        }
      }
    }

    case 'onHoldModal/submit/success': {
      return {
        ...state,
        onHoldModal: {
          ...state.onHoldModal,
          form: {
            ...state.onHoldModal.form,
            reason: ''
          },

          isOpen: false,
          isSubmitting: false,
          error: undefined
        }
      }
    }

    case 'onHoldModal/submit/error': {
      return {
        ...state,
        onHoldModal: {
          ...state.onHoldModal,
          isSubmitting: false,
          error: action.payload
        }
      }
    }

    case 'onHoldModal/form/reason/set': {
      return {
        ...state,
        onHoldModal: {
          ...state.onHoldModal,
          form: {
            ...state.onHoldModal.form,
            reason: action.payload
          }
        }
      }
    }

    default: {
      return state
    }
  }
}
